import { useI18n } from "@common/hooks/i18n";
import toasts from "@common/lib/toast";
import React from "react";

export interface WithSubmitProps<T, R = void> {
  dontCloseAfterSubmit?: boolean;
  submit: (data: T) => Promise<R> | R;
  errorMessage?: string;
  isSubmitting?: boolean;
  onClose?: () => void;
  toast?: string;
}

export default <T, R, P extends WithSubmitProps<T, R>>(
    Modal: React.ComponentType<P>
  ): React.ComponentType<Omit<P, "isSubmitting" | "error" | "onClose"> & { onClose?: () => void }> =>
  ({
    dontCloseAfterSubmit,
    submit,
    ...props
  }: Omit<P, "isSubmitting" | "error" | "onClose"> & {
    onClose?: () => void;
  }) => {
    const i18n = useI18n();

    const isCancelled = React.useRef(false); // https://stackoverflow.com/questions/56442582/react-hooks-cant-perform-a-react-state-update-on-an-unmounted-component
    const [error, setError] = React.useState(undefined);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const _submit = React.useCallback(
      async (data: T) => {
        try {
          setError(undefined);
          setIsSubmitting(true);
          let res = submit(data);
          if (res instanceof Promise) {
            res = await res;
          }
          if (!isCancelled.current) {
            !dontCloseAfterSubmit && props.onClose && props.onClose();

            const finalToast = props.toast ?? i18n.saved;
            finalToast && toasts.info(finalToast);
          }
          return res;
        } catch (e) {
          !isCancelled.current && setError(e.message || e);
          throw e;
        } finally {
          !isCancelled.current && setIsSubmitting(false);
        }
      },
      [submit, dontCloseAfterSubmit, props.onClose, props.toast]
    );

    React.useEffect(() => {
      return () => {
        isCancelled.current = true;
      };
    }, []);

    return <Modal {...(props as P)} submit={_submit} isSubmitting={isSubmitting} errorMessage={error} />;
  };
